import * as styles from "./AmendmentRow.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { ListItem } from "@frontend/components/atoms/List";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import type { ItemAtom } from "@frontend/lib/form";
import {
  formatAmount,
  formatPercent,
  parseAmount,
} from "@frontend/utils/amount";
import { Trans, useLingui } from "@lingui/react/macro";

import type { AmendmentValues } from "../schemas/contract";
import { contractForm } from "../stores/editor";
import { priceChange } from "../utils/amendment";

export type AmendmentRowProps = {
  index: number;
  itemAtom: ItemAtom;
  previousPrice: string | null;
  onOpen: (origin: HTMLElement) => void;
  onRemove: () => void;
};

/**
 * What an amendment amounts to, on one row: the day it takes effect, the price
 * it sets, and what that changes. Writing it happens in the popup the row
 * opens.
 */
export function AmendmentRow({
  index,
  itemAtom,
  previousPrice,
  onOpen,
  onRemove,
}: AmendmentRowProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const errors = useAtomValue(contractForm.state.errors);

  const amendment = useAtomValue(itemAtom) as AmendmentValues;

  const price = parseAmount(amendment.price);
  const change = priceChange(previousPrice, amendment.price);
  const date = formatDate(amendment.dateBegin, locale);

  const incomplete = Object.keys(errors).some((path) =>
    path.startsWith(`amendments.${index}.`),
  );

  const parts = [
    ...(change
      ? [
          `${formatPercent(change.percent, locale)} % ${t`vs`} ${formatAmount(change.previous, locale)} ${t`Frs`}`,
        ]
      : []),
    ...(amendment.comments ? [amendment.comments] : []),
  ];

  return (
    <ListItem
      value={String(index)}
      onClick={(event) => onOpen(event.currentTarget)}
      startSection={<span className={styles.position}>{index + 1}</span>}
      endSection={
        <span className={styles.end}>
          {null === price ? (
            <span className={styles.noPrice}>&ndash;</span>
          ) : (
            <span className={styles.price}>
              {formatAmount(price, locale)} {t`Frs`}
            </span>
          )}

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={false === amendment.deletable}
                  aria-label={t`Remove this amendment`}
                  onClick={(event) => {
                    // The row underneath opens the popup.
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <Icon name="Trash2" size="sm" />
                </Button>
              }
            />

            <TooltipContent>{t`Remove this amendment`}</TooltipContent>
          </Tooltip>
        </span>
      }
    >
      <span className={styles.body}>
        <span className={styles.head}>
          {date ? (
            <span className={styles.date}>{date}</span>
          ) : (
            <span className={styles.undated}>
              <Trans>Amendment to fill in</Trans>
            </span>
          )}

          {incomplete ? (
            <Badge variant="warning" size="sm">
              <Trans>Incomplete</Trans>
            </Badge>
          ) : null}
        </span>

        {0 < parts.length ? (
          <span className={styles.summary}>{parts.join(" · ")}</span>
        ) : null}
      </span>
    </ListItem>
  );
}

function formatDate(value: string | null, locale: string): string | null {
  if (null === value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}
